import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Zap, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      toast.success('Login successful!');
      navigate('/dashboard');
    } else {
      setError(result.error);
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 login-hero relative">
        <div className="absolute inset-0 flex flex-col justify-center px-12 text-white">
          <div className="max-w-lg">
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_battery-central/artifacts/cp6kecw1_voltide_logo_plain.svg" 
                alt="Voltide Logo"
                className="h-20 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-sm text-slate-400/80 tracking-wider uppercase font-heading">BESS Command Center</p>
            </div>
            <h2 className="font-heading text-4xl font-semibold mb-4 leading-tight tracking-tight">
              Battery Energy Storage
              <br />System Monitoring
            </h2>
            <p className="text-lg text-white/90 leading-relaxed">
              Real-time monitoring, intelligent billing, and comprehensive insights for your
              energy storage infrastructure.
            </p>
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div>
                <p className="font-mono text-3xl font-medium text-yellow-400">99.9%</p>
                <p className="text-sm text-white/80 mt-1">Uptime</p>
              </div>
              <div>
                <p className="font-mono text-3xl font-medium text-yellow-400">24/7</p>
                <p className="text-sm text-white/80 mt-1">Monitoring</p>
              </div>
              <div>
                <p className="font-mono text-3xl font-medium text-yellow-400">Real-time</p>
                <p className="text-sm text-white/80 mt-1">Data Sync</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_battery-central/artifacts/cp6kecw1_voltide_logo_plain.svg" 
              alt="Voltide Logo"
              className="h-16 w-auto"
            />
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-sm">
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 mb-2">Welcome back</h2>
              <p className="text-slate-600">Sign in to access your command center</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Login Failed</p>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-slate-700 font-medium">
                  Email Address
                </Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="mt-2 bg-white border-slate-200 focus:ring-slate-900 focus:border-slate-900"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  Password
                </Label>
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="mt-2 bg-white border-slate-200 focus:ring-slate-900 focus:border-slate-900"
                />
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm py-6 text-base font-medium"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>

          <p className="text-center text-sm text-slate-500 mt-6">
            © 2026 Voltide. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};
